import { useEditor, EditorContent } from '@tiptap/react'
import { useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Palette } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { VariableDropdown } from './VariableDropdown'
import { type TemplateVariable } from '@/lib/templateVariables'
import { tokenNodesToText, tokenTextToNodes } from '@/lib/emailTokenHtml'
import { EMAIL_SAFE_FONTS, FONT_SIZES, TEXT_COLORS, EMAIL_DEFAULT_FONT_NAME, EMAIL_DEFAULT_FONT_SIZE, fontFamilyDisplayName, EDITOR_CANVAS_STYLE, EDITOR_CANVAS_PARAGRAPH_CLASS, buildEmailEditorExtensions, emailEditorProps } from '@/lib/emailEditorConfig'

interface EmailBodyEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}

export function EmailBodyEditor({ value, onChange, placeholder }: EmailBodyEditorProps) {
  const { t } = useTranslation('sequences')
  const resolvedPlaceholder = placeholder || t('editor.defaultPlaceholder')
  const extensions = useMemo(
    () => buildEmailEditorExtensions({
      placeholder: resolvedPlaceholder,
      personalizationTokens: true,
      richBlocks: true,
    }),
    [resolvedPlaceholder],
  )
  const editor = useEditor({
    // TipTap v3 non ri-renderizza sui cambi di selezione di default:
    // senza questo la toolbar non riflette font/size del testo su cui clicchi (#228).
    shouldRerenderOnTransaction: true,
    extensions,
    editorProps: emailEditorProps,
    content: tokenTextToNodes(value),
    onUpdate: ({ editor }) => {
      onChange(tokenNodesToText(editor.getHTML()))
    },
  })

  // Sync external value changes (e.g. when switching between steps)
  useEffect(() => {
    if (editor) {
      const currentStored = tokenNodesToText(editor.getHTML())
      if (value !== currentStored) {
        editor.commands.setContent(tokenTextToNodes(value))
      }
    }
  }, [editor, value])

  const handleInsertVariable = useCallback(
    (variable: TemplateVariable, fallback?: string) => {
      editor?.chain().focus().insertToken({
        key: variable.key,
        label: variable.label,
        fallback,
      }).run()
    },
    [editor],
  )

  const handleAddLink = () => {
    const url = window.prompt(t('editor.urlPrompt'))
    if (url) {
      editor?.chain().focus().setLink({ href: url }).run()
    }
  }

  if (!editor) return null

  return (
    <div className="border rounded-md">
      {/* Formatting toolbar */}
      <div className="flex items-center gap-1 p-2 border-b flex-wrap">
        <Select
          value={fontFamilyDisplayName(editor.getAttributes('textStyle').fontFamily) || EMAIL_DEFAULT_FONT_NAME}
          onValueChange={(value) => {
            if (value) {
              editor.chain().focus().setFontFamily(value).run()
            } else {
              editor.chain().focus().unsetFontFamily().run()
            }
          }}
        >
          <SelectTrigger className="h-7 w-[120px] text-xs">
            <SelectValue placeholder={t('editor.font')} />
          </SelectTrigger>
          <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
            {EMAIL_SAFE_FONTS.map((font) => (
              <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={editor.getAttributes('textStyle').fontSize || EMAIL_DEFAULT_FONT_SIZE}
          onValueChange={(value) => {
            if (value) {
              editor.chain().focus().setFontSize(value).run()
            } else {
              editor.chain().focus().unsetFontSize().run()
            }
          }}
        >
          <SelectTrigger className="h-7 w-[72px] text-xs">
            <SelectValue placeholder={t('editor.size')} />
          </SelectTrigger>
          <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
            {FONT_SIZES.map((size) => (
              <SelectItem key={size} value={size}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="w-px h-5 bg-border mx-1" />

        <Button
          type="button"
          variant={editor.isActive('bold') ? 'default' : 'ghost'}
          size="sm"
          className="h-7 w-7 p-0"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('italic') ? 'default' : 'ghost'}
          size="sm"
          className="h-7 w-7 p-0"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('link') ? 'default' : 'ghost'}
          size="sm"
          className="h-7 w-7 p-0"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleAddLink}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
          size="sm"
          className="h-7 w-7 p-0"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
          size="sm"
          className="h-7 w-7 p-0"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0">
              <Palette className="h-3.5 w-3.5" style={{ color: editor.getAttributes('textStyle').color || undefined }} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
            <div className="grid grid-cols-5 gap-1">
              {TEXT_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  title={c.name}
                  className="h-6 w-6 rounded border border-border hover:scale-110 transition-transform"
                  style={{ backgroundColor: c.value }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => editor.chain().focus().setColor(c.value).run()}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-5 bg-border mx-1" />

        <VariableDropdown onInsert={handleInsertVariable} />
      </div>

      {/* Editor area */}
      <EditorContent
        editor={editor}
        style={EDITOR_CANVAS_STYLE}
        className={`max-w-none p-3 min-h-[150px] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 rounded-b-md [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[130px] ${EDITOR_CANVAS_PARAGRAPH_CLASS} [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none`}
      />
    </div>
  )
}
